"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import {
  Check,
  ChevronDown,
  Download,
  FileDown,
  History,
  RefreshCw,
} from "lucide-react";
import {
  AeraButton,
  ModalCloseButton,
} from "@aera/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import WorkerAvatar from "@/app/components/WorkerAvatar";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";

export type PrepareIssueItem = {
  id: string;
  title: string;
  subtitle?: string;
  amountLabel: string;
  href: string;
};

export type PrepareIssueGroup = {
  key: string;
  title: string;
  count: number;
  emptyLabel: string;
  items: PrepareIssueItem[];
};

export type PrepareExportDownload = {
  id: string;
  label: string;
  onClick: () => void;
};

export type PrepareExportRow = {
  id: string;
  version: number;
  whenLabel: string;
  statusLabel: string;
  isLatest: boolean;
  failed?: boolean;
  canDownload: boolean;
  downloads: PrepareExportDownload[];
};

type AccountingPreparePanelProps = {
  onClose: () => void;
  loading: boolean;
  issuesLoading?: boolean;
  error: string | null;
  periodOptions: { value: string; label: string }[];
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  readinessPercent: number;
  readyCount: number;
  needsActionCount: number;
  waitingCount: number;
  correctionCount: number;
  recordedCount: number;
  incomeCount: number;
  expenseCount: number;
  needsReviewCount: number;
  missingEvidenceCount: number;
  statusLabel: string;
  statusClassName?: string;
  nextVersion: number;
  latestVersion?: number;
  busy: boolean;
  canCreate: boolean;
  createDisabledReason?: string;
  onPrepare: () => void;
  onCreateFile: () => void;
  onRefreshIssues?: () => void;
  issuesRefreshing?: boolean;
  onInspect: (classification?: string) => void;
  onViewList: () => void;
  detailsHref: string;
  exportRows: PrepareExportRow[];
  issueGroups: PrepareIssueGroup[];
};

function PeriodSelect({
  value,
  options,
  onSelect,
}: {
  value: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold text-aera-gray">รอบบัญชี</p>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <AeraButton
            type="button"
            variant="outline"
            size="sm"
            fontWeight="semibold"
            fullWidth
            className="!justify-between"
            aria-label={`รอบบัญชี: ${selected?.label || "เลือก"}`}
            aria-expanded={open}
            iconRight={
              <ChevronDown
                size={16}
                className={`text-aera-gray transition-transform duration-200 ${
                  open ? "rotate-180" : "rotate-0"
                }`}
              />
            }
          >
            <span className="min-w-0 flex-1 truncate text-left">
              {selected?.label || "เลือก"}
            </span>
          </AeraButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="z-[10060] max-h-72 min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
        >
          {options.map((opt) => {
            const active = value === opt.value;
            return (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => {
                  onSelect(opt.value);
                  setOpen(false);
                }}
                className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-sm font-semibold text-aera-charcoal"
              >
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                {active ? (
                  <Check
                    className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                    strokeWidth={3}
                    aria-hidden
                  />
                ) : (
                  <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function AccountingPreparePanel({
  onClose,
  loading,
  issuesLoading = false,
  error,
  periodOptions,
  selectedPeriod,
  onPeriodChange,
  readinessPercent,
  readyCount,
  needsActionCount,
  waitingCount,
  correctionCount,
  recordedCount,
  incomeCount,
  expenseCount,
  needsReviewCount,
  missingEvidenceCount,
  statusLabel,
  statusClassName = "bg-aera-offwhite text-aera-gray",
  nextVersion,
  busy,
  canCreate,
  createDisabledReason,
  onPrepare,
  onCreateFile,
  onRefreshIssues,
  issuesRefreshing = false,
  onInspect,
  onViewList,
  detailsHref,
  exportRows,
  issueGroups,
}: AccountingPreparePanelProps) {
  const excludedCount = needsActionCount + waitingCount + correctionCount;

  return (
    <div className="flex h-full min-h-0 flex-col bg-aera-white text-aera-charcoal">
      <header className="shrink-0 px-5 pt-5 pb-4 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-1.5 pt-1 text-xs font-medium text-aera-amberdark">
            <FileDown className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
            <span>ปิดรอบอย่างมีหลักฐาน</span>
          </div>
          <ModalCloseButton
            onClose={onClose}
            className="-mr-0.5 -mt-0.5 h-8! w-8! min-w-0! rounded-xl! border border-aera-lightgray/80 p-0! text-aera-gray hover:bg-aera-offwhite hover:text-aera-charcoal"
          />
        </div>
        <h2 className="mt-3 text-2xl font-bold tracking-tight text-aera-charcoal">
          เตรียมส่งบัญชี
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-aera-gray">
          ใช้ข้อมูลทั้งหมดของพื้นที่นี้ในรอบที่เลือก ไม่ได้ใช้เฉพาะแท็บหรือตัวกรองที่เปิดอยู่
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 sm:px-6">
        {loading ? (
          <PrepareDrawerSkeleton />
        ) : error ? (
          <div className="rounded-2xl bg-aera-redlight px-4 py-3 text-sm text-aera-red">
            {error}
          </div>
        ) : periodOptions.length === 0 ? (
          <div className="rounded-2xl bg-aera-offwhite px-4 py-8 text-center">
            <p className="text-sm font-semibold text-aera-charcoal">
              ยังไม่มีรอบบัญชี
            </p>
            <p className="mt-1 text-xs text-aera-gray">
              เมื่อมีการบันทึกรายรับ/รายจ่าย รอบบัญชีรายเดือนจะแสดงที่นี่
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <PeriodSelect
              value={selectedPeriod}
              options={periodOptions}
              onSelect={onPeriodChange}
            />

            <section className="rounded-2xl border border-aera-lightgray bg-aera-white px-3 py-3">
              <div className="grid grid-cols-5 items-start gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-medium leading-tight text-aera-gray">
                    ความพร้อมรอบนี้
                  </p>
                  <p className="mt-1 text-xl font-bold tabular-nums leading-none text-aera-charcoal">
                    {readinessPercent}%
                  </p>
                  <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-aera-offwhite">
                    <div
                      className="h-full rounded-full bg-aera-amber transition-[width] duration-300"
                      style={{
                        width: `${Math.min(100, Math.max(0, readinessPercent))}%`,
                      }}
                    />
                  </div>
                </div>
                <ReadinessStat value={readyCount} label="ครบถ้วน" />
                <ReadinessStat value={needsActionCount} label="ต้องให้คุณทำ" />
                <ReadinessStat
                  value={waitingCount}
                  label="Bill-Lin กำลังถือ"
                  labelIcon={
                    <WorkerAvatar
                      serviceId={BILLIN_SERVICE_ID}
                      name="Bill-Lin"
                      size={12}
                      className="rounded-full ring-1 ring-aera-green/80"
                    />
                  }
                />
                <ReadinessStat value={correctionCount} label="แก้หลังส่ง" />
              </div>
            </section>

            <div className="grid grid-cols-3 gap-2">
              <AeraButton
                type="button"
                variant="primary"
                size="sm"
                fontWeight="semibold"
                fullWidth
                disabled={busy}
                onClick={onPrepare}
              >
                เตรียมรอบ
              </AeraButton>
              <AeraButton
                type="button"
                variant="warm"
                size="sm"
                fontWeight="semibold"
                fullWidth
                onClick={() => onInspect("needs_review")}
              >
                เปิดตรวจ
              </AeraButton>
              <AeraButton
                type="button"
                variant="outline"
                size="sm"
                fontWeight="semibold"
                fullWidth
                onClick={onViewList}
              >
                ดูรายการ
              </AeraButton>
            </div>

            <section>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-aera-charcoal">
                  เรื่องที่ทำให้รอบนี้ยังไม่พร้อม
                </p>
                {onRefreshIssues ? (
                  <button
                    type="button"
                    onClick={onRefreshIssues}
                    disabled={busy || issuesRefreshing || issuesLoading}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-aera-green disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RefreshCw
                      size={12}
                      className={issuesRefreshing ? "animate-spin" : ""}
                      aria-hidden
                    />
                    อัปเดตอัตโนมัติ
                  </button>
                ) : null}
              </div>
              {issuesLoading ? (
                <IssueGroupsSkeleton />
              ) : (
                <IssueGroupAccordion
                  key={selectedPeriod || "period"}
                  groups={issueGroups}
                />
              )}
            </section>

            <section className="pb-1">
              <p className="text-sm font-semibold text-aera-charcoal">
                ไฟล์ที่ส่งบัญชี
              </p>
              <p className="mt-1 text-sm text-aera-gray">
                รวม {readyCount.toLocaleString("th-TH")} รายการ
                {excludedCount > 0
                  ? ` · แยกออก ${excludedCount.toLocaleString("th-TH")} รายการ`
                  : ""}
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-aera-gray">
                สร้างไฟล์ให้ผู้ทำบัญชี · ยังไม่ได้ลงบัญชี
              </p>
              <AeraButton
                href={detailsHref}
                target="_blank"
                rel="noopener noreferrer"
                variant="link"
                textColor="amberdark"
                fontWeight="semibold"
                className="mt-2 text-xs hover:underline"
              >
                ดูตารางรอบบัญชีทั้งหมด
              </AeraButton>
            </section>

            <section
              className="rounded-2xl border border-aera-lightgray bg-aera-white px-3 py-3"
              data-testid="prepare-period-handoff-summary"
            >
              <div className="grid grid-cols-5 items-start gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-medium leading-tight text-aera-gray">
                    บันทึกแล้ว
                  </p>
                  <p className="mt-1 text-xl font-bold tabular-nums leading-none text-aera-charcoal">
                    {recordedCount.toLocaleString("th-TH")}
                  </p>
                  <p className="mt-1 text-[10px] font-normal tabular-nums leading-tight text-aera-gray">
                    รับ {incomeCount.toLocaleString("th-TH")}
                    {" · "}
                    จ่าย {expenseCount.toLocaleString("th-TH")}
                  </p>
                </div>
                <PeriodHandoffStat
                  value={readyCount}
                  label="พร้อมส่ง"
                  onClick={
                    readyCount > 0 ? () => onInspect("ready") : undefined
                  }
                />
                <PeriodHandoffStat
                  value={needsReviewCount}
                  label="ต้องตรวจ"
                  emphasize={needsReviewCount > 0 ? "amber" : undefined}
                  onClick={
                    needsReviewCount > 0
                      ? () => onInspect("needs_review")
                      : undefined
                  }
                />
                <PeriodHandoffStat
                  value={missingEvidenceCount}
                  label="เอกสารขาด"
                  emphasize={missingEvidenceCount > 0 ? "red" : undefined}
                  onClick={
                    missingEvidenceCount > 0
                      ? () => onInspect("missing_evidence")
                      : undefined
                  }
                />
                <PeriodHandoffStat
                  value={correctionCount}
                  label="แก้ไขหลังส่ง"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-aera-lightgray/80 pt-3">
                <p className="text-[10px] font-medium text-aera-gray">
                  สถานะล่าสุด
                </p>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-nowrap ${statusClassName}`}
                >
                  {statusLabel}
                </span>
                <span className="text-[10px] font-semibold text-aera-gray text-nowrap">
                  ถัดไป v{nextVersion}
                </span>
              </div>
            </section>

            {exportRows.length > 0 ? (
              <ExportHistoryAccordion rows={exportRows} />
            ) : null}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-aera-lightgray bg-aera-white px-5 py-4 sm:px-6">
        {loading ? (
          <div
            className="h-11 w-full animate-pulse rounded-xl bg-aera-lightgray/70"
            aria-hidden
          />
        ) : (
          <AeraButton
            type="button"
            variant="secondary"
            size="md"
            fontWeight="semibold"
            fullWidth
            disabled={busy || !selectedPeriod || !canCreate}
            title={
              !canCreate
                ? createDisabledReason || "ยังสร้างไฟล์ไม่ได้"
                : `สร้างไฟล์ v${nextVersion} ให้ผู้ทำบัญชี — ยังไม่ใช่การลงบัญชี`
            }
            onClick={onCreateFile}
          >
            {busy
              ? "กำลังทำงาน…"
              : `สร้างไฟล์ v.${nextVersion} (${readyCount.toLocaleString("th-TH")} รายการ)`}
          </AeraButton>
        )}
      </div>
    </div>
  );
}

function SkeletonBar({ className }: { className: string }) {
  return <div className={`animate-pulse rounded bg-aera-lightgray/70 ${className}`} />;
}

function IssueGroupsSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-aera-lightgray"
      role="status"
      aria-live="polite"
      aria-label="กำลังโหลดรายการที่ยังไม่พร้อม"
    >
      <IssueGroupSkeleton expanded itemCount={3} />
      <div className="border-t border-aera-lightgray">
        <IssueGroupSkeleton />
      </div>
      <div className="border-t border-aera-lightgray">
        <IssueGroupSkeleton />
      </div>
    </div>
  );
}

function IssueGroupSkeleton({
  expanded = false,
  itemCount = 0,
}: {
  expanded?: boolean;
  itemCount?: number;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 bg-aera-offwhite px-3.5 py-3">
        <SkeletonBar className="h-4 w-28" />
        <SkeletonBar className="ml-auto h-6 min-w-6 rounded-full" />
        <SkeletonBar className="h-4 w-4 shrink-0" />
      </div>
      {expanded ? (
        <div className="bg-aera-white px-3.5 pb-2 pt-0.5">
          {Array.from({ length: itemCount }).map((_, index) => (
            <div
              key={index}
              className={`flex items-center justify-between gap-3 py-2.5 ${
                index > 0 ? "border-t border-aera-lightgray/70" : ""
              }`}
            >
              <div className="min-w-0 flex-1 space-y-1.5">
                <SkeletonBar className="h-3.5 w-[70%]" />
                <SkeletonBar className="h-2.5 w-24" />
              </div>
              <SkeletonBar className="h-3.5 w-10 shrink-0" />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PrepareDrawerSkeleton() {
  return (
    <div
      className="flex flex-col gap-5"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="กำลังโหลดรอบบัญชี"
    >
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold text-aera-gray">รอบบัญชี</p>
        <SkeletonBar className="h-10 w-full rounded-xl" />
      </div>

      <section className="rounded-2xl border border-aera-lightgray bg-aera-white px-3 py-3">
        <div className="grid grid-cols-5 items-start gap-2">
          <div className="min-w-0">
            <SkeletonBar className="h-2.5 w-[90%]" />
            <SkeletonBar className="mt-1.5 h-5 w-10" />
            <SkeletonBar className="mt-1.5 h-1 w-full rounded-full" />
          </div>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="min-w-0">
              <SkeletonBar className="h-2.5 w-full max-w-[3.5rem]" />
              <SkeletonBar className="mt-1.5 h-5 w-6" />
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-3 gap-2">
        <SkeletonBar className="h-9 w-full rounded-xl" />
        <SkeletonBar className="h-9 w-full rounded-xl" />
        <SkeletonBar className="h-9 w-full rounded-xl" />
      </div>

      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-aera-charcoal">
            เรื่องที่ทำให้รอบนี้ยังไม่พร้อม
          </p>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-aera-green/50">
            <RefreshCw size={12} aria-hidden />
            อัปเดตอัตโนมัติ
          </span>
        </div>
        <IssueGroupsSkeleton />
      </section>

      <section className="pb-1">
        <SkeletonBar className="h-4 w-28" />
        <SkeletonBar className="mt-2 h-3.5 w-48" />
        <SkeletonBar className="mt-2 h-2.5 w-56" />
        <SkeletonBar className="mt-3 h-3 w-40" />
      </section>

      <section className="rounded-2xl border border-aera-lightgray bg-aera-white px-3 py-3">
        <div className="grid grid-cols-5 items-start gap-2">
          <div className="min-w-0">
            <SkeletonBar className="h-2.5 w-[90%]" />
            <SkeletonBar className="mt-1.5 h-5 w-8" />
            <SkeletonBar className="mt-1 h-2.5 w-16" />
          </div>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="min-w-0">
              <SkeletonBar className="h-2.5 w-full max-w-[3.5rem]" />
              <SkeletonBar className="mt-1.5 h-5 w-6" />
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-1.5 border-t border-aera-lightgray/80 pt-3">
          <SkeletonBar className="h-2.5 w-14" />
          <SkeletonBar className="h-5 w-20 rounded-full" />
          <SkeletonBar className="h-2.5 w-12" />
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-aera-lightgray">
        <div className="flex items-center gap-2 bg-aera-offwhite px-3.5 py-3">
          <SkeletonBar className="h-4 w-4 shrink-0 rounded" />
          <SkeletonBar className="h-4 w-32" />
          <SkeletonBar className="ml-auto h-6 min-w-6 rounded-full" />
          <SkeletonBar className="h-4 w-4 shrink-0" />
        </div>
        <ul className="bg-aera-white">
          {Array.from({ length: 3 }).map((_, index) => (
            <li
              key={index}
              className="flex items-center gap-3 border-t border-aera-lightgray px-3.5 py-2.5"
            >
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex items-center gap-2">
                  <SkeletonBar className="h-3.5 w-10" />
                  {index === 0 ? (
                    <SkeletonBar className="h-4 w-10 rounded-full" />
                  ) : null}
                </div>
                <SkeletonBar className="h-2.5 w-40 max-w-full" />
              </div>
              <div className="flex shrink-0 gap-1">
                <SkeletonBar className="h-6 w-14 rounded-full" />
                <SkeletonBar className="h-6 w-14 rounded-full" />
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function IssueGroupAccordion({
  groups,
}: {
  groups: PrepareIssueGroup[];
}) {
  const populatedKeys = useMemo(
    () =>
      groups
        .filter((group) => group.items.length > 0 || group.count > 0)
        .map((group) => group.key),
    [groups],
  );
  const [openKeys, setOpenKeys] = useState<Set<string>>(
    () => new Set(populatedKeys),
  );
  const seededRef = useRef(populatedKeys.length > 0);

  useEffect(() => {
    if (seededRef.current) return;
    if (populatedKeys.length === 0) return;
    seededRef.current = true;
    setOpenKeys(new Set(populatedKeys));
  }, [populatedKeys]);

  const toggle = (key: string) => {
    seededRef.current = true;
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div
      className="overflow-hidden rounded-2xl border border-aera-lightgray"
      data-testid="prepare-issue-accordion"
    >
      {groups.map((group, index) => {
        const count = Math.max(group.count, group.items.length);
        const hasItems = count > 0;
        const open = openKeys.has(group.key);
        const panelId = `prepare-issue-${group.key}`;
        return (
          <div
            key={group.key}
            className={
              index < groups.length - 1 ? "border-b border-aera-lightgray" : ""
            }
            data-testid={`prepare-issue-card-${group.key}`}
          >
            <button
              type="button"
              onClick={() => toggle(group.key)}
              aria-expanded={open}
              aria-controls={panelId}
              className="flex w-full items-center gap-2 bg-aera-offwhite px-3.5 py-3 text-left"
            >
              {group.key === "waiting" ? (
                <WorkerAvatar
                  serviceId={BILLIN_SERVICE_ID}
                  name="Bill-Lin"
                  size={18}
                  className="rounded-full ring-1 ring-aera-green/80"
                />
              ) : null}
              <span className="min-w-0 flex-1 text-sm font-semibold text-aera-charcoal">
                {group.title}
              </span>
              <span className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-aera-white px-2 text-xs font-bold tabular-nums text-aera-charcoal ring-1 ring-aera-lightgray/80">
                {count.toLocaleString("th-TH")}
              </span>
              <ChevronDown
                size={16}
                className={`shrink-0 text-aera-gray transition-transform duration-200 ${
                  open ? "rotate-0" : "-rotate-90"
                }`}
                aria-hidden
              />
            </button>
            <div
              id={panelId}
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
              aria-hidden={!open}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="bg-aera-white px-3.5 pb-2 pt-0.5">
                  {hasItems ? (
                    group.items.length > 0 ? (
                      <ul
                        className="flex flex-col"
                        data-testid={`prepare-issue-list-${group.key}`}
                      >
                        {group.items.map((item, itemIndex) => (
                          <li
                            key={item.id}
                            className={
                              itemIndex > 0
                                ? "border-t border-aera-lightgray/70"
                                : ""
                            }
                          >
                            <Link
                              href={item.href}
                              className="group flex items-center justify-between gap-3 py-2.5 text-sm text-aera-charcoal transition-colors hover:text-aera-amber"
                            >
                              <span className="min-w-0 flex-1 leading-snug">
                                <span className="block font-semibold">
                                  {item.title}
                                </span>
                                {item.subtitle ? (
                                  <span className="mt-0.5 block text-xs font-normal text-aera-gray transition-colors group-hover:text-aera-amber">
                                    {item.subtitle}
                                  </span>
                                ) : null}
                              </span>
                              <span className="shrink-0 font-semibold tabular-nums">
                                {item.amountLabel}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="py-3 text-center text-sm text-aera-gray">
                        {group.emptyLabel}
                      </p>
                    )
                  ) : (
                    <p className="py-3 text-center text-sm text-aera-gray">
                      {group.emptyLabel}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExportHistoryAccordion({ rows }: { rows: PrepareExportRow[] }) {
  const [open, setOpen] = useState(true);
  const panelId = "prepare-export-history";

  return (
    <section
      className="overflow-hidden rounded-2xl border border-aera-lightgray"
      data-testid="prepare-export-history-accordion"
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-2 bg-aera-offwhite px-3.5 py-3 text-left"
      >
        <History className="h-4 w-4 shrink-0 text-aera-gray" aria-hidden />
        <span className="min-w-0 flex-1 text-sm font-semibold text-aera-charcoal">
          ประวัติไฟล์รอบนี้
        </span>
        <span className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-aera-white px-2 text-xs font-bold tabular-nums text-aera-charcoal ring-1 ring-aera-lightgray/80">
          {rows.length.toLocaleString("th-TH")}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-aera-gray transition-transform duration-200 ${
            open ? "rotate-0" : "-rotate-90"
          }`}
          aria-hidden
        />
      </button>
      <div
        id={panelId}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          <ul className="bg-aera-white">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex items-center gap-3 border-t border-aera-lightgray px-3.5 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-aera-charcoal">
                    v{row.version}
                    {row.isLatest ? (
                      <span className="ml-2 rounded-full bg-aera-greenlight px-2 py-0.5 text-[10px] font-semibold text-aera-green">
                        ล่าสุด
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-aera-gray">
                    {row.whenLabel} · {row.statusLabel}
                  </p>
                </div>
                {row.canDownload && row.downloads.length > 0 ? (
                  <div className="flex shrink-0 flex-wrap justify-end gap-1">
                    {row.downloads.map((file) => (
                      <AeraButton
                        key={file.id}
                        type="button"
                        variant="outline"
                        size="xxs"
                        fontWeight="semibold"
                        className="whitespace-nowrap rounded-full!"
                        icon={<Download size={12} />}
                        onClick={file.onClick}
                      >
                        {file.label}
                      </AeraButton>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ReadinessStat({
  value,
  label,
  labelIcon,
}: {
  value: number;
  label: string;
  labelIcon?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-start gap-0.5 text-[10px] font-medium leading-tight text-aera-gray">
        {labelIcon ? (
          <span className="mt-px inline-flex shrink-0" aria-hidden>
            {labelIcon}
          </span>
        ) : null}
        <span>{label}</span>
      </div>
      <p className="mt-1 text-xl font-bold tabular-nums leading-none text-aera-charcoal">
        {value.toLocaleString("th-TH")}
      </p>
    </div>
  );
}

function PeriodHandoffStat({
  value,
  label,
  emphasize,
  onClick,
}: {
  value: number;
  label: string;
  emphasize?: "amber" | "red";
  onClick?: () => void;
}) {
  const valueClass =
    emphasize === "amber"
      ? "text-aera-amber"
      : emphasize === "red"
        ? "text-aera-red"
        : "text-aera-charcoal";
  const content = (
    <>
      <p className="text-[10px] font-medium leading-tight text-aera-gray">
        {label}
      </p>
      <span
        className={`mt-1 block text-xl font-bold tabular-nums leading-none ${valueClass}`}
      >
        {value.toLocaleString("th-TH")}
      </span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="min-w-0 rounded-lg text-left transition-opacity hover:opacity-80"
        title={`เปิดรายการ${label}ของรอบนี้`}
      >
        {content}
      </button>
    );
  }
  return <div className="min-w-0">{content}</div>;
}
